import { FC } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowDown, faArrowUp } from '@fortawesome/free-solid-svg-icons'
import { faThumbsDown } from '@fortawesome/free-regular-svg-icons'
import { ReactComponent as GlobeIcon } from 'src/assets/svg/akar-icons_globe.svg'
import { ReactComponent as LightbulbIcon } from 'src/assets/svg/lightbulb-line-24.svg'
import { ReactComponent as MentionIcon } from 'src/assets/svg/akar-icons_mention.svg'
import { ReactComponent as SmileIcon } from 'src/assets/svg/uil_smile.svg'
import { TruncatedTooltip } from 'src/components/TruncatedTooltip'
import { cn } from 'src/utils/cn'
import styles from './keyMetricsComparison.module.scss'
import { KeyMetric } from '../interfaces/comparison.interface'
import { deltaDirection } from '../delta'

interface KeyMetricsComparisonProps {
  comparisonLabel: string
  // One line summarising the four cards, as written by the report.
  insight: string
  metrics: KeyMetric[]
  primaryLabel: string
  // Campaign Comparison reuses this card to compare CAMPAIGNS rather than the
  // two periods of one campaign, and has to be able to say so.
  subtitle?: string
}

const COPY = {
  subtitle:
    'Track how key performance indicators changed between the selected periods.',
  title: 'Key Metrics Comparison',
}

// Keyed by the metric's `icon` field so the data stays free of components.
const METRIC_ICONS = {
  globe: GlobeIcon,
  mention: MentionIcon,
  smile: SmileIcon,
}

export const KeyMetricsComparison: FC<KeyMetricsComparisonProps> = ({
  comparisonLabel,
  insight,
  metrics,
  primaryLabel,
  subtitle,
}) => (
  <section className={styles.section}>
    <div className={styles.header}>
      <div className={styles.headerText}>
        <h3 className={styles.title}>{COPY.title}</h3>
        <p className={styles.subtitle}>{subtitle ?? COPY.subtitle}</p>
      </div>

      {/* Which colour belongs to which period — the same pairing the values
          inside every card use. */}
      <div className={styles.legend}>
        <span className={styles.legendItem}>
          <span
            className={cn(styles.legendSwatch, styles.legendSwatchPrimary)}
          />
          <TruncatedTooltip text={primaryLabel} />
        </span>

        <span className={styles.legendItem}>
          <span
            className={cn(styles.legendSwatch, styles.legendSwatchComparison)}
          />
          <TruncatedTooltip text={comparisonLabel} />
        </span>
      </div>
    </div>

    <div className={styles.cards}>
      {metrics.map((metric) => {
        const Icon = METRIC_ICONS[metric.icon as keyof typeof METRIC_ICONS]
        const direction = deltaDirection(metric.delta)

        return (
          <article className={styles.card} key={metric.label}>
            <div className={styles.cardHeader}>
              {Icon ? (
                <Icon className={styles.cardIcon} />
              ) : (
                <FontAwesomeIcon
                  className={styles.cardIcon}
                  icon={faThumbsDown}
                />
              )}
              <span className={styles.cardLabel}>{metric.label}</span>
            </div>

            <div className={styles.values}>
              <div className={styles.value}>
                <span className={styles.valueNumber}>
                  {metric.primaryValue}
                </span>
                {/* A campaign's name rarely fits the half-card it is given,
                    so the label ellipsises — and carries the full name as its
                    tooltip, since a truncated name is not an identification. */}
                <TruncatedTooltip
                  className={styles.valuePeriod}
                  text={metric.primaryPeriod}
                />
              </div>

              <span className={styles.valueDivider} />

              <div className={cn(styles.value, styles.valueComparison)}>
                <span className={styles.valueNumber}>
                  {metric.comparisonValue}
                </span>
                <TruncatedTooltip
                  className={styles.valuePeriod}
                  text={metric.comparisonPeriod}
                />
              </div>
            </div>

            {/* Colour and arrow both follow the number: up is green, down is
                red, unchanged is neither. */}
            <span
              className={cn(styles.delta, {
                [styles.deltaGood]: direction === 'up',
                [styles.deltaBad]: direction === 'down',
                [styles.deltaFlat]: direction === 'flat',
              })}
            >
              {direction !== 'flat' && (
                <FontAwesomeIcon
                  className={styles.deltaArrow}
                  icon={direction === 'up' ? faArrowUp : faArrowDown}
                />
              )}
              {metric.delta}
            </span>
          </article>
        )
      })}
    </div>

    <div className={styles.insight}>
      <LightbulbIcon className={styles.insightIcon} />
      <p className={styles.insightText}>{insight}</p>
    </div>
  </section>
)
